import { FC, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { WhatsappCampaignFormInput } from '../../WhatsappCampaignValidation';
import {
  PrepareConnectorFlowStepResponse,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { ActionButton } from './ActionButton';
import ErrorBoundary from '@/components/ErrorBoundary';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCompanyValues } from '@/providers';
import { MultiStepFlowForm } from './MultiStepFlowFormv2';
import { FlowMappingsForm } from './FlowMappingsForm';
import { StepPageActions } from '@/global/components/Layout/StepPageActions/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { FlowFormData, FlowMappingsConfig, FlowMappingsData } from './types';
import { WHATSAPP_CONNECTOR_FLOW_NAME } from './constants';
import { useTranslation } from 'react-i18next';

type TemplateSelectionStepProps = {
  onBack: () => void;
  onNext: (formData: FlowFormData, mappingsData: FlowMappingsData) => Promise<boolean>;
};

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

export const TemplateSelectionStep: FC<TemplateSelectionStepProps> = ({ onBack, onNext }) => {
  const { id: workspaceId } = useWorkspace();
  const { setValue, watch } = useFormContext<WhatsappCampaignFormInput>();
  const selectedAudienceId = watch('audienceId');

  const { t } = useTranslation('campaign');

  // ✅ Separate loading/error/result state for the mutation result
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [multiStepsCompleted, setMultiStepsCompleted] = useState(false);

  // GET ConnectorFlow:
  const { data: connectorFlows } = useGetConnectorFlowsQuery({
    workspaceId,
    queryParams: {
      showUserJourney: true,
    },
  });
  const {
    data: connectorInstances,
    isLoading: isLoadingInstances,
    refetch: refetchInstances,
  } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });

  const { currentLanguage } = useCompanyValues();

  // Take Out the Id of the connectorFlows which matches name with whatsappConnectorFlowName and connectorId with whatsappConnectorId
  const whatsappConnectorFlow = connectorFlows?.find((flow) => flow.name === WHATSAPP_CONNECTOR_FLOW_NAME);
  const whatsappConnectorFlowId = whatsappConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === whatsappConnectorFlow?.connectorId)?.id;

  // Fetch the prepare data when we have the required IDs
  const [prepareStep, { isLoading: isPreparing }] = usePrepareConnectorFlowStepMutation();

  useEffect(() => {
    setValue('connectorId', whatsappConnectorFlow?.connectorId || '');
    setValue('connectorFlowId', whatsappConnectorFlowId || '');
  }, [whatsappConnectorFlowId, setValue]);

  // ✅ Stable callback ref so useEffect deps stay clean
  const fetchPrepareData = useCallback(async () => {
    if (!selectedAudienceId || !whatsappConnectorFlowId) return;
    if (!connectorInstanceId) {
      setPrepareError('No WhatsApp connector instance found. Please connect WhatsApp first and retry.');
      return;
    }

    setPrepareError(null);

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: whatsappConnectorFlowId,
        audienceId: selectedAudienceId,
        stepId: 'step1',
        connectorInstanceId,
        stepData: {}, // any additional data needed for preparation
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      // const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;

      // ✅ Store the result so schema/uiSchema are actually available for render
      setPrepareResult(prepareResultResponse as PrepareStepResult);
    } catch (_err) {
      setPrepareError('Failed to load template options. Please try again.');
      setPrepareResult(null);
    }
  }, [selectedAudienceId, whatsappConnectorFlowId, connectorInstanceId, prepareStep, workspaceId, currentLanguage]);

  useEffect(() => {
    setPrepareResult(null);
    setPrepareError(null);
    setFormData({});
    setMultiStepMappingsConfig(null);
    setMappingData({});
    setIsMappingsFormValid(false);
    setMultiStepsCompleted(false);
  }, [selectedAudienceId, whatsappConnectorFlowId, connectorInstanceId]);

  useEffect(() => {
    if (!whatsappConnectorFlowId || !selectedAudienceId) return;
    fetchPrepareData();
  }, [fetchPrepareData, whatsappConnectorFlowId, selectedAudienceId, connectorInstanceId]);

  const multiStepFlowFormProps = {
    audienceId: selectedAudienceId!,
    connectorFlowId: whatsappConnectorFlowId!,
    connectorInstanceId: connectorInstanceId!,
    paramsConfig: prepareResult || {},
    onComplete: async (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
      setSubmitError(null);
      setFormData(allParams);
      setMultiStepMappingsConfig(mappingsConfig);
      setMappingData({});
      setIsMappingsFormValid(false);
      setMultiStepsCompleted(true);
      return;
    },
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const handleFinalSubmit = async () => {
    if (!isMappingsFormValid) {
      return;
    }
    await onNext(formData, mappingData);
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON:cancel' as any,
    onClick: onBack,
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON:confirm' as any,
    onClick: handleFinalSubmit,
    disabled: !isMappingsFormValid,
  };

  const pageTitle = t('whatsappWorkspace.selectTemplatePageTitle');

  // ✅ Guard: don't show loading until we actually have IDs to fetch
  if (!whatsappConnectorFlowId) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>Initializing...</p>
      </div>
    );
  }

  if (isPreparing) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>Loading template options...</p>
      </div>
    );
  }

  if (!connectorInstanceId && !isLoadingInstances) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-red-600'>{t('whatsappWorkspace.noWhatsappConnector')}</p>
        <ActionButton handleBack={onBack} handleNext={async () => void (await refetchInstances())} isNextDisabled={false} isLoading={false} />
      </div>
    );
  }

  // ✅ prepareError is now null by default, not {}
  if (prepareError) {
    return (
      <div className='space-y-8'>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-red-600'>{prepareError}</p>
        <ActionButton handleBack={onBack} handleNext={fetchPrepareData} isNextDisabled={false} isLoading={false} />
      </div>
    );
  }

  const noFinalMappingAvailable = !multiStepMappingsConfig?.schema?.properties || Object.keys(multiStepMappingsConfig?.schema.properties).length === 0;

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{pageTitle}</h2>
        <p className='mt-2 text-sm text-gray-600'>{t('whatsappWorkspace.selectTemplatePageDescription')}</p>
      </div>
      {!multiStepsCompleted && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm {...multiStepFlowFormProps} />
          ) : (
            <p className='text-sm text-gray-600'>{t('whatsappWorkspace.noTemplatesFound')}</p>
          )}
        </ErrorBoundary>
      )}
      {submitError && <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{submitError}</div>}
      <ErrorBoundary>
        {(() => {
          // Use mappings from last step if available, otherwise fall back to static config
          if (noFinalMappingAvailable) {
            return null;
          }
          return (
            <div>
              <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.dataMapping')}</h3>
              <FlowMappingsForm
                config={multiStepMappingsConfig}
                formData={mappingData}
                onFormDataChange={handleMappingDataChange}
                onValidationChange={handleMappingsValidationChange}
              />
              <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
            </div>
          );
        })()}
      </ErrorBoundary>
    </div>
  );
};
